import {MAX_PINNED_ALIASES, MAX_PINNED_PROPERTIES} from './entityCardConstants';

function toPinnedPropertySnapshot(property) {
    if (!property?.propertyItemId) return null;
    return {
        propertyItemId: property.propertyItemId,
        propertyLabel: property.propertyLabel || property.propertyId || 'Property',
        value: property.value == null ? '' : String(property.value),
    };
}

/**
 * Add a property snapshot to the card pin list (cap {@link MAX_PINNED_PROPERTIES}).
 * @returns {{next: Array, changed: boolean, reason?: string}}
 */
export function pinPropertySnapshot(pinnedProperties, property) {
    const current = Array.isArray(pinnedProperties) ? pinnedProperties : [];
    const snapshot = toPinnedPropertySnapshot(property);
    if (!snapshot) {
        return {next: current, changed: false, reason: 'missing-id'};
    }
    if (current.some((item) => item.propertyItemId === snapshot.propertyItemId)) {
        return {next: current, changed: false, reason: 'already-pinned'};
    }
    if (current.length >= MAX_PINNED_PROPERTIES) {
        return {next: current, changed: false, reason: 'at-cap'};
    }
    return {next: [...current, snapshot], changed: true};
}

/**
 * Remove a pinned property by propertyItemId.
 */
export function unpinPropertySnapshot(pinnedProperties, propertyItemId) {
    const current = Array.isArray(pinnedProperties) ? pinnedProperties : [];
    if (!propertyItemId) return {next: current, changed: false};
    const next = current.filter((item) => item.propertyItemId !== propertyItemId);
    return {next, changed: next.length !== current.length};
}

/**
 * Refresh denormalized pin snapshots from live properties (edit) or drop deleted ones.
 */
export function refreshPinnedPropertySnapshots(pinnedProperties, liveProperties) {
    const current = Array.isArray(pinnedProperties) ? pinnedProperties : [];
    if (current.length === 0) return {next: current, changed: false};

    const byId = new Map();
    for (const property of liveProperties || []) {
        if (property?.propertyItemId) {
            byId.set(property.propertyItemId, property);
        }
    }

    let changed = false;
    const next = [];
    for (const pinned of current) {
        const live = byId.get(pinned.propertyItemId);
        if (!live) {
            changed = true;
            continue;
        }
        const snapshot = toPinnedPropertySnapshot(live);
        if (
            snapshot.propertyLabel !== pinned.propertyLabel
            || snapshot.value !== pinned.value
        ) {
            changed = true;
        }
        next.push(snapshot);
    }

    return {next, changed};
}

function normalizeAliasText(alias) {
    if (typeof alias !== 'string') return '';
    return alias.trim();
}

/**
 * Pin an alias string onto the card (cap {@link MAX_PINNED_ALIASES}).
 * @returns {{next: string[], changed: boolean, reason?: string}}
 */
export function pinAlias(pinnedAliases, alias) {
    const current = Array.isArray(pinnedAliases) ? pinnedAliases : [];
    const text = normalizeAliasText(alias);
    if (!text) {
        return {next: current, changed: false, reason: 'empty'};
    }
    if (current.some((item) => normalizeAliasText(item).toLowerCase() === text.toLowerCase())) {
        return {next: current, changed: false, reason: 'already-pinned'};
    }
    if (current.length >= MAX_PINNED_ALIASES) {
        return {next: current, changed: false, reason: 'at-cap'};
    }
    return {next: [...current, text], changed: true};
}

/**
 * Unpin an alias string (case-insensitive match).
 */
export function unpinAlias(pinnedAliases, alias) {
    const current = Array.isArray(pinnedAliases) ? pinnedAliases : [];
    const text = normalizeAliasText(alias);
    if (!text) return {next: current, changed: false};
    const next = current.filter(
        (item) => normalizeAliasText(item).toLowerCase() !== text.toLowerCase(),
    );
    return {next, changed: next.length !== current.length};
}

/**
 * Drop pinned aliases that no longer exist in the live known-alias list.
 */
export function refreshPinnedAliases(pinnedAliases, liveAliases) {
    const current = Array.isArray(pinnedAliases) ? pinnedAliases : [];
    if (current.length === 0) return {next: current, changed: false};

    const liveSet = new Set(
        (liveAliases || [])
            .map(normalizeAliasText)
            .filter(Boolean)
            .map((alias) => alias.toLowerCase()),
    );

    const next = current.filter((alias) => {
        const text = normalizeAliasText(alias);
        return text && liveSet.has(text.toLowerCase());
    });

    return {next, changed: next.length !== current.length};
}

function normalizeIsoTimestamp(value) {
    if (typeof value !== 'string') return '';
    return value.trim();
}

/**
 * Pin the KB last-updated snapshot onto the card.
 * @param {string|null|undefined} kbLastUpdatedAt ISO timestamp
 * @returns {{kbLastUpdatedPinned: boolean, kbLastUpdatedAt: string}}
 */
export function pinKbLastUpdated(kbLastUpdatedAt) {
    return {
        kbLastUpdatedPinned: true,
        kbLastUpdatedAt: normalizeIsoTimestamp(kbLastUpdatedAt),
    };
}

/**
 * Clear the KB last-updated pin and stored snapshot.
 * @returns {{kbLastUpdatedPinned: boolean, kbLastUpdatedAt: string}}
 */
export function unpinKbLastUpdated() {
    return {
        kbLastUpdatedPinned: false,
        kbLastUpdatedAt: '',
    };
}
